import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  searchable?: boolean;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchable = false,
  label,
  required,
  disabled,
  error,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Anchored by `top` when opening below, or by `bottom` when flipped
  // above — anchoring the bottom edge lets the box hug the trigger and
  // size to its content. The Sort control on the Property page sits in
  // the bottom pagination row, so a downward-only dropdown was either
  // invisible or floated mid-air above the trigger.
  const [dropdownStyle, setDropdownStyle] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  }>({ top: 0, left: 0, width: 0, maxHeight: 320 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  const filteredOptions = searchable
    ? options.filter((o) => o.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : options;

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inContainer && !inDropdown) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen, searchable]);

  function handleSelect(optionValue: string) {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  }

  function handleToggle() {
    if (!disabled) {
      if (!isOpen) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const margin = 16; // viewport breathing room
          const gap = 4;
          const spaceBelow = window.innerHeight - rect.bottom - margin;
          const spaceAbove = rect.top - margin;
          const placeAbove = spaceBelow < 200 && spaceAbove > spaceBelow;
          const maxHeight = Math.min(320, Math.max(120, placeAbove ? spaceAbove : spaceBelow));
          setDropdownStyle(
            placeAbove
              ? {
                  bottom: window.innerHeight - rect.top + gap,
                  left: rect.left,
                  width: rect.width,
                  maxHeight,
                }
              : {
                  top: rect.bottom + gap,
                  left: rect.left,
                  width: rect.width,
                  maxHeight,
                },
          );
        }
        setSearchQuery('');
      }
      setIsOpen((prev) => !prev);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          {label}
          {required && <span className="text-error-500 ml-0.5">*</span>}
        </label>
      )}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2 text-sm bg-white dark:bg-gray-800 border rounded-lg transition-colors text-left',
          isOpen
            ? 'border-primary-500 ring-2 ring-primary-500 outline-none'
            : error
              ? 'border-error-500 hover:border-error-600'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500',
          disabled && 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-900',
          !selectedOption && 'text-gray-400 dark:text-gray-500'
        )}
      >
        <span className={cn('truncate', selectedOption ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500')}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {error && (
        <p className="mt-1.5 text-sm text-error-600">{error}</p>
      )}

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          // Height is precomputed in handleToggle() against the chosen
          // placement (below or flipped above the trigger) so the
          // dropdown stays inside the viewport even when opened from
          // the bottom of the page (e.g., the Sort control in the
          // Property page pagination footer).
          style={{
            position: 'fixed',
            top: dropdownStyle.top,
            bottom: dropdownStyle.bottom,
            left: dropdownStyle.left,
            width: dropdownStyle.width,
            maxHeight: dropdownStyle.maxHeight,
          }}
          className="flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[1000] overflow-hidden"
        >
          {searchable && (
            <div className="flex-shrink-0 p-2 border-b border-gray-100 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-7 pr-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-6 text-sm text-gray-400 text-center">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 text-sm text-left transition-colors',
                    'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700',
                    option.description ? 'py-2.5' : 'py-2',
                    option.value === value && 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  )}
                >
                  <div>
                    <span>{option.label}</span>
                    {option.description && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{option.description}</p>
                    )}
                  </div>
                  {option.value === value && <Check className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400 flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
